// The dashboard assistant's ops answer, read one way by the rail's card (Chat/agentic/segments.ts)
// and the editor's Undo gate (dashboard-assistant-polish-3 requirement 5). A pure module: no
// fetch, no store, so the chat reducer's node tests can load it.

import type { DraftOp } from "./dashboardDraft";

/**
 * A filter that applied while some widgets of its tab had not rendered: it may change nothing in
 * them yet. A tab with no widget yet comes with no `widget_ids` (dashboard-assistant-polish-4).
 */
export interface UncheckedFilter {
  /** The tab the filter was set on; absent from an answer older than per-tab filters. */
  tab_id?: string;
  widget_ids: string[];
}

/**
 * An `apply_dashboard_ops` answer: the version it left the draft at, what applied, what was
 * declined and why, and the widgets an applied filter could not be checked against. It carries
 * no config; the editor reads the draft again for that.
 */
export interface OpsAnswer {
  version: number;
  applied: DraftOp[];
  declined: Array<{ op: DraftOp; reason: string }>;
  unchecked: UncheckedFilter[];
  /**
   * Library widgets the call placed with a look of their own (#13714 L1.6), by the id it gave
   * each; absent when there were none.
   */
  from_library?: Array<{ widget_id: string; name: string }>;
}

const objectList = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (v): v is Record<string, unknown> =>
          typeof v === "object" && v !== null && !Array.isArray(v)
      )
    : [];

/** A tool result's data as an ops answer, or null when it is not one. */
export const opsResultOf = (data: unknown): OpsAnswer | null => {
  if (typeof data !== "object" || data === null || Array.isArray(data))
    return null;
  const r = data as Record<string, unknown>;
  if (typeof r.version !== "number" || !Array.isArray(r.applied)) return null;
  return {
    version: r.version,
    applied: objectList(r.applied) as DraftOp[],
    declined: objectList(r.declined).map((d) => ({
      op: d.op as DraftOp,
      reason:
        typeof d.reason === "string" && d.reason.trim()
          ? d.reason.trim()
          : "It could not be done.",
    })),
    unchecked: objectList(r.unchecked).map((u) => ({
      ...(typeof u.tab_id === "string" ? { tab_id: u.tab_id } : {}),
      widget_ids: Array.isArray(u.widget_ids)
        ? u.widget_ids.filter((id): id is string => typeof id === "string")
        : [],
    })),
    ...fromLibraryOf(r.from_library),
  };
};

const fromLibraryOf = (value: unknown): Pick<OpsAnswer, "from_library"> => {
  const placed = objectList(value).flatMap((p) =>
    typeof p.widget_id === "string" && typeof p.name === "string"
      ? [{ widget_id: p.widget_id, name: p.name }]
      : []
  );
  return placed.length > 0 ? { from_library: placed } : {};
};
